import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { isEmptyArray, isEmptyObject, isStringEmpty, numberZeroUndefined, stringEmptyUndefined } from '@common/helpers';
import {
  DEFAULT_IMMIGRATION_EXPIRY_DATE,
  ImmigrationSecondDocumentType,
  ImmigrationStayType,
  MailDestinationType,
  PhoneDestinationType,
  RegistrationLabelType,
  SmsChangeWarningMessage,
  SmsChangeWarningMessageOnPhoneNumberChange,
  WHEEL_CHAIR_BATTERY_CODE,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { ApiErrorMap } from '@common/interfaces';
import {
  ArrivalDepartureNoticeInformationData,
  ArrivalDepartureNoticeInformationEmail,
} from '@common/interfaces/mybooking/modals/passenger-information-input/arrival-departure-notice-information.interface';
import {
  DuringStayInformationData,
  DuringStayInformationParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/during-stay-information.interface';
import {
  ImmigrationInformationData,
  ImmigrationInformationParts,
  RegulatoryDetailsDestinationAddress,
  RegulatoryDetailsGreenCard,
  RegulatoryDetailsVisa,
} from '@common/interfaces/mybooking/modals/passenger-information-input/immigration-information.interface';
import {
  ApiResponse,
  UpdateTravelersRequestContactsTravelers,
  UpdateTravelersRequestContactsTravelersDepartuteArrivalNotifications,
  UpdateTravelersRequestContactsType,
  UpdateTravelersRequestFrequentFlyerCards,
  UpdateTravelersRequestPassengerSupports,
  UpdateTravelersRequestSecondaryDocument,
  UpdateTravelersRequestTravelers,
  UpdateTravelersResult,
} from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-Information-input-modal.interface';
import { PassengerInformationGroup } from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-information.interface';
import { PassportInformationData } from '@common/interfaces/mybooking/modals/passenger-information-input/passport-information.interface';
import {
  PhoneNumberEventType,
  PhoneTypeEventType,
  RepresentativeInformationData,
  RepresentativeInformationParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/representative-information.interface';
import {
  UpgradeNotificationEmailData,
  UpgradeNotificationEmailParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/upgrade-notification-email.interface';
import { selectMyBookingState } from '@common/store/my-booking/my-booking.selectors';
import { MyBookingState } from '@common/store/my-booking/my-booking.state';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SupportClass } from '@lib/components/support-class';
import { ApiCommonRequest, ErrorType, NotRetryableErrorModel, PageType, RetryableError } from '@lib/interfaces';
import { ApiErrorResponseModel } from '@lib/interfaces/api-error-response';
import { DialogClickType } from '@lib/interfaces/dialog';
import { AswServiceStoreService } from '@lib/services/asw-service-store/asw-service-store.service';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { DialogDisplayService } from '@lib/services/dialog-display/dialog-display.service';
import { Store } from '@ngrx/store';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { MemberApiService, IFlyMemberInfo } from 'src/sdk-member';
import {
  GetOrderRequest,
  GetOrderResponse,
  GetOrderResponseDataAir,
  GetOrderResponseDataContactsRepresentativePhonesInner,
  GetOrderResponseDataOrderType,
  DefaultService as ServicingApiService,
  UpdateTravelersRequest,
  UpdateTravelersRequestContactsRepresentativePhone,
} from 'src/sdk-servicing';
import { GetOrderResponseData } from 'src/sdk-servicing/model/getOrderResponseData';
import { DuringStayInformationService } from './during-stay-information.service';
import { PassengerInformationInputModalCommonService } from './passenger-information-input-modal.common.service';
import { PassengerInformationService } from './passenger-information.service';
import { RepresentativeInformationService } from './representative-information.service';
import { UpgradeNotificationEmailService } from './upgrade-notification-email.service';
import { GetMemberInformationState } from '@lib/store';
import { ErrorCodeConstants } from '@conf/app.constants';

const UPDATE_TRAVELERS_API_ERROR_MAP: ApiErrorMap = {
  [ErrorCodeConstants.ERROR_CODES.EBAZ000106]: {
    isRetryableError: false,
    errorMsgId: 'E0341',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000631]: {
    isRetryableError: false,
    errorMsgId: 'E0410',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000722]: {
    isRetryableError: false,
    errorMsgId: 'E1722',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000303]: {
    isRetryableError: true,
    errorMsgId: 'E0774',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000304]: {
    isRetryableError: true,
    errorMsgId: 'E0775',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000406]: {
    isRetryableError: true,
    errorMsgId: 'E0483',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000407]: {
    isRetryableError: true,
    errorMsgId: 'E0781',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000408]: {
    isRetryableError: true,
    errorMsgId: 'E0491',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000409]: {
    isRetryableError: true,
    errorMsgId: 'E0782',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000410]: {
    isRetryableError: true,
    errorMsgId: 'E0783',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000411]: {
    isRetryableError: true,
    errorMsgId: 'E0784',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000413]: {
    isRetryableError: true,
    errorMsgId: 'E0785',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000414]: {
    isRetryableError: true,
    errorMsgId: 'E0786',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000416]: {
    isRetryableError: true,
    errorMsgId: 'E0777',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000417]: {
    isRetryableError: true,
    errorMsgId: 'E0778',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000418]: {
    isRetryableError: true,
    errorMsgId: 'E0779',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000419]: {
    isRetryableError: true,
    errorMsgId: 'E0468',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000713]: {
    isRetryableError: true,
    errorMsgId: 'E1239',
  },
  /**
   * 不要 →　内部設計書更新連携中
   * EBAZ000404: {
   *   isRetryableError: true,
   *   errorMsgId: 'EXXXX',
   * },
   */
  [ErrorCodeConstants.ERROR_CODES.EBAZ000708]: {
    isRetryableError: true,
    errorMsgId: 'E1240',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000709]: {
    isRetryableError: true,
    errorMsgId: 'E1241',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000707]: {
    isRetryableError: true,
    errorMsgId: 'E1243',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000298]: {
    isRetryableError: true,
    errorMsgId: 'E0457',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000544]: {
    isRetryableError: true,
    errorMsgId: 'E1236',
  },
  [ErrorCodeConstants.ERROR_CODES.EBAZ000820]: {
    isRetryableError: true,
    errorMsgId: 'E1827',
  },
  // 提携: 会員情報の姓名と画面入力値が一致しない
  [ErrorCodeConstants.ERROR_CODES.EBAA000028]: {
    isRetryableError: true,
    errorMsgId: 'EA056',
  },
  // 提携: ALLIANCE_INC-59: デッドロジック
  // 提携: NAMEエレメントの生年月日を修正することができず、搭乗者情報の更新ができなかった
  [ErrorCodeConstants.ERROR_CODES.EBAA000037]: {
    isRetryableError: true,
    errorMsgId: 'EA061',
  },
};

/**
 * 搭乗者情報 service
 */
@Injectable()
export class PassengerInformationInputModalService extends SupportClass {
  // #region initial state of data, save for checking at (44) 更新ボタン押下処理
  public initRepresentativeInfoData!: RepresentativeInformationData;
  public initUpgradeNotifyEmailData!: UpgradeNotificationEmailData;
  public listOfInitPassengerInfoData: Array<PassengerInformationGroup> = [];
  public initDuringStayInfoData!: DuringStayInformationData;
  // #endregion

  subscription: Subscription = new Subscription();

  constructor(
    private readonly _common: CommonLibService,
    private readonly _store: Store,
    private readonly _datePipe: DatePipe,
    private readonly _servicingApiService: ServicingApiService,
    private readonly _memberApiService: MemberApiService,
    private readonly _aswServiceStoreService: AswServiceStoreService,
    private readonly _dialogService: DialogDisplayService,
    private readonly _representativeInfoService: RepresentativeInformationService,
    private readonly _upgradeNotificationEmailService: UpgradeNotificationEmailService,
    private readonly _passengerInfoService: PassengerInformationService,
    private readonly _duringStayInfoService: DuringStayInformationService,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService
  ) {
    super();
  }

  /**
   * 提供された Observable リクエストで API エンドポイントを呼び出し、ApiResponse を含む Promise を返します。
   * @param request$ API リクエストを表す Observable。
   * @returns ApiResponse を含む Promise。
   */
  callApi<T>(request$: Observable<T>): Promise<ApiResponse<T>> {
    return new Promise((resolve) => {
      this.subscription.add(
        request$.pipe(take(1)).subscribe({
          next: (response: T) => {
            return resolve({ model: response });
          },
          error: (error: ApiErrorResponseModel) => {
            return resolve({ error: error });
          },
        })
      );
    });
  }

  /**
   * 会員情報取得API処理
   * @returns 会員情報 Promise。
   */
  async callGetMemberInformationApi(): Promise<IFlyMemberInfo | undefined> {
    const promiseEvent = () =>
      new Promise<GetMemberInformationState>((resolve) => {
        this._common.amcMemberStoreService.saveMemberInformationToAMCMember$().subscribe((result) => {
          resolve(result);
        });
      });
    const result = await promiseEvent();
    return result?.model?.data;
  }

  /**
   * PNR情報取得API処理
   * @param isMask マスキング要否。<br/> 指定なしの場合true：マスキング要とする
   * @returns PNR情報 Promise。
   */
  async callGetOrderApi(isMask: boolean = false): Promise<GetOrderResponse | undefined> {
    const param = this.createGetOrderApiRequestParam(isMask);

    const apiResponse = await this.callApi(this._servicingApiService.ordersGetOrderPost(param));

    if (apiResponse.error) {
      this.handleGetOrderApiError();
      return undefined;
    }

    return apiResponse.model;
  }

  /**
   * create PNR情報取得APIリクエストデータ型
   * @param isMask マスキング要否。<br/> 指定なしの場合true：マスキング要とする
   * @returns PNR情報取得APIリクエストデータ型
   */
  createGetOrderApiRequestParam(isMask: boolean): GetOrderRequest {
    const aswServiceData = this._aswServiceStoreService.aswServiceData;

    return {
      ...(aswServiceData?.travelDocumentId
        ? {
            travelDocumentId: aswServiceData.travelDocumentId,
          }
        : {
            orderId: aswServiceData.orderId ?? '',
          }),
      credential: {
        firstName: aswServiceData.firstName ?? '',
        lastName: aswServiceData.lastName ?? '',
      },
      mask: isMask,
      getServiceCatalogue: true,
    };
  }

  /**
   * PNR情報取得API失敗時の処理
   */
  handleGetOrderApiError() {
    const apiErrorCode = this._common.apiError?.errors?.[0]?.code ?? '';
    const errorInfo = {
      errorType: ErrorType.BUSINESS_LOGIC,
      errorMsgId: 'E0400',
      apiErrorCode,
    };

    this._common.errorsHandlerService.setNotRetryableError(errorInfo);
  }

  /**
   * 各ブロックの登録状況に応じたAPIリクエストパラメータ作成処理
   * @param passengersInformationDataGroup
   * @param representativeInformationData
   * @param upgradeNotificationEmailData
   * @param duringStayInformationData
   * @returns 搭乗者情報更新APIのリクエストボディ
   */
  createUpdateDataParam(
    passengersInformationDataGroup: Array<PassengerInformationGroup>,
    representativeInformationData: RepresentativeInformationData,
    upgradeNotificationEmailData: UpgradeNotificationEmailData,
    duringStayInformationData: DuringStayInformationData
  ): UpdateTravelersRequest {
    const aswServiceData = this._aswServiceStoreService.aswServiceData;
    let apiRequestParam: UpdateTravelersRequest = {
      orderId: aswServiceData.orderId ?? '',
      credential: {
        firstName: aswServiceData.firstName ?? '',
        lastName: aswServiceData.lastName ?? '',
      },
      isUpdateBasicReservationInformation: false,
    };

    // 画面.当該搭乗者情報.パスポート情報.AMC会員情報更新チェックボックスがチェックありの場合、true
    apiRequestParam.isUpdateBasicReservationInformation =
      passengersInformationDataGroup?.find(
        (passengerDataGroup) => passengerDataGroup?.parts?.passportInformationParts?.isDisplayUpdateAMCMember
      )?.data.passports?.[0]?.updateAMCMemberInfo ?? false;

    apiRequestParam.contacts = this.getContactRequestParam(
      passengersInformationDataGroup,
      representativeInformationData,
      duringStayInformationData,
      upgradeNotificationEmailData
    );

    if (passengersInformationDataGroup.length > 0) {
      apiRequestParam.travelers = this.getTravelerRequestParam(passengersInformationDataGroup);

      const frequentFlyerCardRequestParam = this.getFrequentFlyerCardRequestParam(passengersInformationDataGroup);

      if (!isEmptyObject(frequentFlyerCardRequestParam)) {
        apiRequestParam.frequentFlyerCards = frequentFlyerCardRequestParam;
      }

      const passengerSupportRequestParam = this.getPassengerSupportRequestParam(passengersInformationDataGroup);

      if (!isEmptyObject(passengerSupportRequestParam)) {
        apiRequestParam.passengerSupports = passengerSupportRequestParam;
      }
    }

    return apiRequestParam;
  }

  compareObjects(original: any, modified: any, parent?: string): any {
    if (typeof original === 'object' && original !== null) {
      if (Array.isArray(original) && Array.isArray(modified)) {
        let changes = [];
        const length = Math.max(original.length, modified.length);
        for (let i = 0; i < length; i++) {
          const result = this.compareObjects(original?.[i], modified?.[i]);
          if (result !== undefined) {
            changes.push(result);
            if (parent === 'secondaryDocuments') {
              changes[changes.length - 1].applicableCountryCode = modified?.[i]?.applicableCountryCode;
            }
            if (parent === 'contactDuringStay' || parent === 'emails') {
              return modified;
            }
            // ※発着通知メール送信言語が非表示、または発着通知メール送信言語が未選択の場合、ユーザ共通情報.操作言語を設定する。
            if (parent === 'departureArrivalNotifications') {
              return modified?.map((d) => ({
                email: {
                  address: d?.email?.address ?? '',
                },
                recipient: d?.recipient ?? '',
                lang: d?.lang ?? this._common.aswContextStoreService.aswContextData.lang,
              }));
            }
          } else if (parent === 'departureArrivalNotifications') {
            if (original?.length === 2 && modified?.length === 1 && i === 1) {
              const result = this.compareObjects(original?.[1], modified?.[0]);
              if (result !== undefined) {
                changes.push(result);
                return modified?.map((d) => ({
                  email: {
                    address: d?.email?.address ?? '',
                  },
                  recipient: d?.recipient ?? '',
                  lang: d?.lang ?? this._common.aswContextStoreService.aswContextData.lang,
                }));
              }
            }
          }
        }
        return changes.length > 0 ? changes : undefined;
      } else {
        let changes: any = {};
        for (let key in original) {
          if (key in original) {
            const result = this.compareObjects(original?.[key], modified?.[key], key);
            if (result !== undefined) {
              changes[key] = result;
              if (key === 'contactDuringStay') {
                changes[key] = modified['contactDuringStayOperation'] === 'cancel' ? undefined : changes[key];
                changes['contactDuringStayOperation'] = modified['contactDuringStayOperation'];
              }
              if (key === 'phone') {
                changes[key] = modified['phoneOperation'] === 'cancel' ? undefined : modified[key];
                changes['phoneOperation'] = modified['phoneOperation'];
              }
              if (key === 'representative') {
                changes[key] = {
                  ...changes[key],
                  ...(changes[key]?.phone ? { phone: modified[key]?.phone } : {}),
                };
              }
              if (key === 'departureArrivalNotifications') {
                changes[key] =
                  modified['departureArrivalNotificationsOperation'] === 'cancel' ? undefined : changes[key];
                changes['departureArrivalNotificationsOperation'] = modified['departureArrivalNotificationsOperation'];
              }
              if (key === 'chineaseCitizenIdentificationNumber') {
                changes[key] =
                  modified['chineseCitizenIdentificationNumberOperation'] === 'cancel' ? undefined : changes[key];
                changes['chineseCitizenIdentificationNumberOperation'] =
                  modified['chineseCitizenIdentificationNumberOperation'];
              }
              if (key === 'visa') {
                changes[key] = modified['visaOperation'] === 'cancel' ? undefined : modified[key];
                changes['visaOperation'] = modified['visaOperation'];
                if (modified['addressOperation'] === 'cancel') {
                  changes['addressOperation'] = modified['addressOperation'];
                  changes['address'] = { stayOrTransit: 'stayInUS' };
                }
              }
              if (key === 'greenCard') {
                changes[key] = modified['greenCardOperation'] === 'cancel' ? undefined : modified[key];
                changes['greenCardOperation'] = modified['greenCardOperation'];
                if (modified['addressOperation'] === 'cancel') {
                  changes['addressOperation'] = modified['addressOperation'];
                  changes['address'] = { stayOrTransit: 'stayInUS' };
                }
              }
              if (key === 'passport') {
                changes[key] = {
                  ...(changes[key]?.name?.firstName ||
                  changes[key]?.name?.middleName ||
                  changes[key]?.name?.lastName ||
                  changes[key]?.birthDate ||
                  changes[key]?.gender ||
                  changes[key]?.nationalityCode
                    ? {
                        name: modified[key].name,
                        birthDate: modified[key].birthDate,
                        gender: modified[key].gender,
                        nationalityCode: modified[key].nationalityCode,
                        ...(original[key].number ? { number: modified[key].number } : {}),
                        ...(original[key].expiryDate ? { expiryDate: modified[key].expiryDate } : {}),
                        ...(original[key].issuanceCountryCode
                          ? { issuanceCountryCode: modified[key].issuanceCountryCode }
                          : {}),
                      }
                    : {}),
                  ...(changes[key]?.number || changes[key]?.expiryDate || changes[key]?.issuanceCountryCode
                    ? {
                        name: modified[key].name,
                        birthDate: modified[key].birthDate,
                        gender: modified[key].gender,
                        nationalityCode: modified[key].nationalityCode,
                        number: modified[key].number,
                        expiryDate: modified[key].expiryDate,
                        issuanceCountryCode: modified[key].issuanceCountryCode,
                      }
                    : {}),
                };
              }
              if (key === 'address' && typeof changes[key] === 'object') {
                changes['addressOperation'] = modified['addressOperation'];
                changes[key] =
                  modified.addressOperation === 'request'
                    ? {
                        stayOrTransit: modified?.address?.stayOrTransit,
                        ...(modified?.address?.stayOrTransit === ImmigrationStayType.STAY_IN_US && {
                          line: modified?.address?.line,
                          cityName: modified?.address?.cityName,
                          stateCode: modified?.address?.stateCode,
                          zipCode: modified?.address?.zipCode,
                        }),
                      }
                    : undefined;
              }
              if (key === 'walking') {
                changes[key] =
                  modified['walkingOperation'] === 'cancel'
                    ? undefined
                    : {
                        degreeSpecialServiceRequest: {
                          code: modified?.walking?.degreeSpecialServiceRequest?.code,
                        },
                        wheelChair: {
                          ...(modified?.walking?.wheelChair?.isNeeded
                            ? {
                                ...changes[key].wheelChair,
                                isFoldable: modified?.walking?.wheelChair?.isFoldable,
                                type: modified?.walking?.wheelChair?.type,
                                ...(modified?.walking?.wheelChair?.type === WHEEL_CHAIR_BATTERY_CODE && {
                                  batteryType: modified?.walking?.wheelChair?.batteryType,
                                }),
                                ...(!Boolean(modified?.walking?.wheelChair?.isFoldable) && {
                                  depth: modified?.walking?.wheelChair?.depth,
                                  width: modified?.walking?.wheelChair?.width,
                                  height: modified?.walking?.wheelChair?.height,
                                  weight: modified?.walking?.wheelChair?.weight,
                                }),
                              }
                            : {}),
                          isNeeded: modified?.walking?.wheelChair?.isNeeded ?? false,
                        },
                      };
                changes['walkingOperation'] = modified['walkingOperation'];
              }
              if (key === 'pregnant') {
                changes[key] = modified['pregnantOperation'] === 'cancel' ? undefined : modified[key];
                changes['pregnantOperation'] = modified['pregnantOperation'];
              }
              if (key === 'email') {
                changes[key] = {
                  address: modified?.[key]?.address ?? '',
                };
              }
              if (key === 'fqtv') {
                changes[key] = {
                  ...changes[key],
                  companyCode: modified[key].companyCode,
                };
              }
              if (key === 'secondContact') {
                changes[key] = modified[key];
              }
            }
          }
        }
        for (let key in modified) {
          if (key in modified && !(key in original)) {
            changes[key] = this.compareObjects(original?.[key], modified?.[key]);
            if (key === 'contactDuringStay') {
              changes[key] = modified['contactDuringStayOperation'] === 'cancel' ? undefined : changes[key];
              changes['contactDuringStayOperation'] = modified['contactDuringStayOperation'];
            }
            if (key === 'phone') {
              changes[key] = modified['phoneOperation'] === 'cancel' ? undefined : modified[key];
              changes['phoneOperation'] = modified['phoneOperation'];
            }
            if (key === 'representative') {
              changes[key] = {
                ...changes[key],
                ...(changes[key]?.phone ? { phone: modified[key]?.phone } : {}),
              };
            }
            if (key === 'departureArrivalNotifications') {
              changes[key] = modified['departureArrivalNotificationsOperation'] === 'cancel' ? undefined : changes[key];
              changes['departureArrivalNotificationsOperation'] = modified['departureArrivalNotificationsOperation'];
            }
            if (key === 'chineaseCitizenIdentificationNumber') {
              changes[key] =
                modified['chineseCitizenIdentificationNumberOperation'] === 'cancel' ? undefined : changes[key];
              changes['chineseCitizenIdentificationNumberOperation'] =
                modified['chineseCitizenIdentificationNumberOperation'];
            }
            if (key === 'visa') {
              changes[key] = modified['visaOperation'] === 'cancel' ? undefined : modified[key];
              changes['visaOperation'] = modified['visaOperation'];
            }
            if (key === 'greenCard') {
              changes[key] = modified['greenCardOperation'] === 'cancel' ? undefined : modified[key];
              changes['greenCardOperation'] = modified['greenCardOperation'];
            }
            if (key === 'passport') {
              changes[key] = {
                ...(changes[key]?.name?.firstName ||
                changes[key]?.name?.middleName ||
                changes[key]?.name?.lastName ||
                changes[key]?.birthDate ||
                changes[key]?.gender ||
                changes[key]?.nationalityCode
                  ? {
                      name: modified[key].name,
                      birthDate: modified[key].birthDate,
                      gender: modified[key].gender,
                      nationalityCode: modified[key].nationalityCode,
                    }
                  : {}),
                ...(changes[key]?.number || changes[key]?.expiryDate || changes[key]?.issuanceCountryCode
                  ? {
                      name: modified[key].name,
                      birthDate: modified[key].birthDate,
                      gender: modified[key].gender,
                      nationalityCode: modified[key].nationalityCode,
                      number: modified[key].number,
                      expiryDate: modified[key].expiryDate,
                      issuanceCountryCode: modified[key].issuanceCountryCode,
                    }
                  : {}),
              };
            }
            if (key === 'address' && typeof changes[key] === 'object') {
              changes['addressOperation'] = modified['addressOperation'];
              changes[key] =
                modified.addressOperation === 'request'
                  ? {
                      stayOrTransit: modified?.address?.stayOrTransit,
                      ...(modified?.address?.stayOrTransit === ImmigrationStayType.STAY_IN_US && {
                        line: modified?.address?.line,
                        cityName: modified?.address?.cityName,
                        stateCode: modified?.address?.stateCode,
                        zipCode: modified?.address?.zipCode,
                      }),
                    }
                  : undefined;
            }
            if (key === 'walking') {
              changes[key] =
                modified['walkingOperation'] === 'cancel'
                  ? undefined
                  : {
                      degreeSpecialServiceRequest: {
                        code: modified?.walking?.degreeSpecialServiceRequest?.code,
                      },
                      wheelChair: {
                        ...(modified?.walking?.wheelChair?.isNeeded
                          ? {
                              ...changes[key].wheelChair,
                              isFoldable: modified?.walking?.wheelChair?.isFoldable,
                              type: modified?.walking?.wheelChair?.type,
                              ...(modified?.walking?.wheelChair?.type === WHEEL_CHAIR_BATTERY_CODE && {
                                batteryType: modified?.walking?.wheelChair?.batteryType,
                              }),
                              ...(!Boolean(modified?.walking?.wheelChair?.isFoldable) && {
                                depth: modified?.walking?.wheelChair?.depth,
                                width: modified?.walking?.wheelChair?.width,
                                height: modified?.walking?.wheelChair?.height,
                                weight: modified?.walking?.wheelChair?.weight,
                              }),
                            }
                          : {}),
                        isNeeded: modified?.walking?.wheelChair?.isNeeded ?? false,
                      },
                    };
              changes['walkingOperation'] = modified['walkingOperation'];
            }
            if (key === 'pregnant') {
              changes[key] = modified['pregnantOperation'] === 'cancel' ? undefined : modified[key];
              changes['pregnantOperation'] = modified['pregnantOperation'];
            }
            if (key === 'email') {
              changes[key] = {
                address: modified?.[key]?.address ?? '',
              };
            }
            if (key === 'fqtv') {
              changes[key] = {
                ...changes[key],
                companyCode: modified[key].companyCode,
              };
            }
            if (key === 'secondContact') {
              changes[key] = modified[key];
            }
          }
        }

        return Object.keys(changes).length > 0 ? changes : undefined;
      }
    } else if (isStringEmpty(original) && isStringEmpty(modified)) {
      return undefined;
    } else if (original !== modified) {
      return modified;
    }
    return undefined;
  }

  createUpdateTravelersApiRequestParam(requestParam: UpdateTravelersRequest): UpdateTravelersRequest | undefined {
    const initParam = this.createUpdateDataParam(
      this.listOfInitPassengerInfoData,
      this.initRepresentativeInfoData,
      this.initUpgradeNotifyEmailData,
      this.initDuringStayInfoData
    );
    const aswServiceData = this._aswServiceStoreService.aswServiceData;
    if (this.compareObjects(initParam, requestParam)) {
      return {
        orderId: aswServiceData.orderId ?? '',
        credential: {
          firstName: aswServiceData.firstName ?? '',
          lastName: aswServiceData.lastName ?? '',
        },
        isUpdateBasicReservationInformation: false,
        ...this.compareObjects(initParam, requestParam),
      };
    }
    return undefined;
  }

  /**
   * 連絡先情報更新のためのAPIリクエストパラメータ作成処理
   * @param passengers
   * @param representativeInfoData
   * @param duringStayInfoData
   * @param upgradeNotificationEmailData
   * @returns 搭乗者情報更新APIのリクエストパラメータ 連絡先情報
   */
  getContactRequestParam(
    passengers: Array<PassengerInformationGroup>,
    representativeInfoData: RepresentativeInformationData,
    duringStayInfoData: DuringStayInformationData,
    upgradeNotificationEmailData: UpgradeNotificationEmailData
  ): UpdateTravelersRequestContactsType {
    let contact: UpdateTravelersRequestContactsType = {};

    const representativeContactRequestParam = this.createRepresentativeContactRequestParam(
      representativeInfoData,
      duringStayInfoData
    );

    if (!isEmptyObject(representativeContactRequestParam)) {
      contact.representative = representativeContactRequestParam;
    }

    if (passengers.length === 0) {
      return contact;
    }

    passengers.forEach((passenger) => {
      const passengerContactRequestParam = this.createPassengerContactRequestParam(passenger);

      if (!isEmptyObject(passengerContactRequestParam)) {
        contact[passenger.data.travelerId] = passengerContactRequestParam;
      }
    });

    return contact;
  }

  /**
   * 代表連絡先および滞在中連絡のリクエストパラメータを作成します。
   * @param representativeInfoData 代表情報データ。
   * @param duringStayInfoData 滞在中の情報データ。
   * @returns 代表連絡先情報のリクエストパラメータ。
   */
  createRepresentativeContactRequestParam(
    representativeInfoData: RepresentativeInformationData,
    duringStayInfoData: DuringStayInformationData
  ) {
    const email = representativeInfoData.emails?.[0];
    const phone = representativeInfoData.phones?.[0];
    return {
      ...(email && {
        email: {
          address: email.email,
        },
      }),
      ...(phone && {
        phone: {
          type: phone.type as UpdateTravelersRequestContactsRepresentativePhone.TypeEnum,
          countryPhoneExtension: phone.phoneCountryNumber,
          number: phone.phoneNumber.replace(/-/g, ''),
        },
      }),
    };
  }

  /**
   * 乗客の連絡先情報のリクエストパラメータを作成します。
   * @param passenger 乗客情報グループ。
   * @returns 乗客の連絡先情報のリクエストパラメータ。
   */
  createPassengerContactRequestParam(passenger: PassengerInformationGroup) {
    const passengerContact = passenger.data.contact;
    const passengerSecondContact = passengerContact.secondContact;
    const isSecondContactOwnerOfPhoneRegistered = !isStringEmpty(passengerSecondContact.ownerOfPhone);
    const isSecondContactPhoneRegistered =
      !isStringEmpty(passengerSecondContact.countryPhoneExtension) &&
      !isStringEmpty(passengerSecondContact.phonesNumber);
    const isSecondContactRegistered = isSecondContactOwnerOfPhoneRegistered || isSecondContactPhoneRegistered;

    let result: UpdateTravelersRequestContactsTravelers = {
      ...(!isStringEmpty(passengerContact.passengerMailAddress) && {
        email: {
          address: passengerContact.passengerMailAddress,
        },
      }),
      ...(!isStringEmpty(passengerContact.passengerSMSCountryNumber) &&
        !isStringEmpty(passengerContact.passengerSMSNumber) && {
          phone: {
            countryPhoneExtension: passengerContact.passengerSMSCountryNumber,
            number: passengerContact.passengerSMSNumber,
          },
        }),
      phoneOperation: passengerContact.passengerSMSDestination === PhoneDestinationType.NOT_SEND ? 'cancel' : 'request',
      ...(isSecondContactRegistered && {
        secondContact: {
          ...(isSecondContactPhoneRegistered && {
            phone: {
              ...(!isStringEmpty(passengerSecondContact.countryPhoneExtension) && {
                countryPhoneExtension: passengerSecondContact.countryPhoneExtension,
              }),
              ...(!isStringEmpty(passengerSecondContact.phonesNumber) && {
                number: passengerSecondContact.phonesNumber,
              }),
            },
          }),
          ...(isSecondContactOwnerOfPhoneRegistered && {
            ownerOfPhone: passengerSecondContact.ownerOfPhone,
          }),
        },
      }),
    };

    let departureArrivalNotifyRequestParam: Array<UpdateTravelersRequestContactsTravelersDepartuteArrivalNotifications> =
      [];

    passenger.data?.arrivalAndDepartureNotice?.emails?.forEach((email, i) => {
      const address = email.address;
      const mailAddressRegistered = !isStringEmpty(address);
      const recipient = email.recipient;
      const mailRecipientNameRegistered = !isStringEmpty(recipient);
      const lang = email.lang;
      const mailLangRegistered = !isStringEmpty(lang);

      if (mailAddressRegistered || mailRecipientNameRegistered || mailLangRegistered) {
        departureArrivalNotifyRequestParam.push({
          ...(mailAddressRegistered && { email: { address } }),
          ...(mailRecipientNameRegistered && { recipient }),
          ...(mailLangRegistered && { lang }),
        });
      }
    });

    if (departureArrivalNotifyRequestParam.length > 0) {
      result.departureArrivalNotifications = departureArrivalNotifyRequestParam;
      result.departureArrivalNotificationsOperation = 'request';
    } else {
      result.departureArrivalNotifications = [];
      result.departureArrivalNotificationsOperation = 'cancel';
    }

    return result;
  }

  /**
   * 搭乗者情報更新のためのAPIリクエストパラメータ作成処理
   * @param passengers 乗客情報グループ。
   * @returns 搭乗者情報更新APIのリクエストパラメータ 搭乗者情報
   */
  getTravelerRequestParam(passengers: Array<PassengerInformationGroup>): Array<UpdateTravelersRequestTravelers> {
    let travelers: Array<UpdateTravelersRequestTravelers> = [];

    passengers.forEach((passenger) => {
      const passport = passenger.data.passports?.[0];
      const immigration = passenger.data.immigration;
      const immigrationParts = passenger.parts.immigrationInformationParts;
      const homeAddressCountry = immigration.homeAddressCountry?.countries?.[0];
      const chineseCitizenIdentificationNumber = immigration.chineseCitizenIdentificationNumber?.numbers?.[0];
      const redressNumber = immigration.redressNumber?.numbers?.[0];
      const knownTravellerNumber = immigration.knownTravellerNumber?.numbers?.[0];
      let traveler: UpdateTravelersRequestTravelers = {
        [passenger.data.travelerId]: {},
      };

      const secondaryDocumentsRequest = this.createTravelerSecondaryDocumentsRequest(immigration, immigrationParts);

      traveler[passenger.data.travelerId].regulatoryDetails = {
        passport: this.createPassportRequest(passport),
        ...(homeAddressCountry && {
          homeAddressCountryCode: homeAddressCountry.code,
        }),

        ...(chineseCitizenIdentificationNumber && {
          chineaseCitizenIdentificationNumber: chineseCitizenIdentificationNumber,
        }),
        chineseCitizenIdentificationNumberOperation: isStringEmpty(chineseCitizenIdentificationNumber)
          ? 'cancel'
          : 'request',
        ...(secondaryDocumentsRequest.length > 0 && {
          secondaryDocuments: secondaryDocumentsRequest,
        }),
        ...(redressNumber && {
          redressNumber: redressNumber,
        }),
        ...(knownTravellerNumber && {
          knownTravellerNumber: knownTravellerNumber,
        }),
      };

      if (isEmptyObject(traveler[passenger.data.travelerId].regulatoryDetails)) {
        traveler[passenger.data.travelerId] = {};
      }

      traveler[passenger.data.travelerId].birthDate =
        passenger.data.basicInformation.selectYmd && !isNaN(passenger.data.basicInformation.selectYmd.getTime())
          ? this._datePipe.transform(passenger.data.basicInformation.selectYmd, 'yyyy-MM-dd') ?? ''
          : undefined;

      travelers.push(traveler);
    });

    return travelers;
  }

  /**
   * パスポート情報に基づいてパスポート要求オブジェクトを作成します。
   * @param passport パスポート情報。
   * @returns パスポート要求オブジェクト。
   */
  createPassportRequest(passport?: PassportInformationData) {
    const isNotEmptyPassport =
      !isStringEmpty(passport?.firstName) ||
      !isStringEmpty(passport?.middleName) ||
      !isStringEmpty(passport?.lastName) ||
      !isStringEmpty(passport?.birthDate) ||
      !isStringEmpty(passport?.gender) ||
      !isStringEmpty(passport?.nationalityCode) ||
      !isStringEmpty(passport?.number) ||
      !isStringEmpty(passport?.expiryDate) ||
      !isStringEmpty(passport?.issuanceCountryCode);
    if (isNotEmptyPassport) {
      return {
        name: {
          firstName: passport?.firstName,
          middleName: passport?.middleName,
          lastName: passport?.lastName,
        },
        birthDate:
          passport?.birthDate !== null ? this._datePipe.transform(passport?.birthDate, 'yyyy-MM-dd') ?? '' : '',
        gender: passport?.gender,
        nationalityCode: passport?.nationalityCode,
        number: passport?.number,
        expiryDate:
          passport?.expiryDate !== null ? this._datePipe.transform(passport?.expiryDate, 'yyyy-MM-dd') ?? '' : '',
        issuanceCountryCode: passport?.issuanceCountryCode,
      };
    }
    return {};
  }

  /**
   * 入国管理情報と設定に基づいて旅行者の二次書類のリクエストを作成します。
   * @param immigration 入国関連情報
   * @param immigrationParts 入国関連情報ブロック 設定値
   * @returns 二次ドキュメント リクエストの配列。
   */
  createTravelerSecondaryDocumentsRequest(
    immigration: ImmigrationInformationData,
    immigrationParts: ImmigrationInformationParts
  ): UpdateTravelersRequestSecondaryDocument[] {
    let result: UpdateTravelersRequestSecondaryDocument[] = [];

    immigration.regulatoryDetails.forEach((regulatory, arrivalCountry) => {
      const secondDocument: UpdateTravelersRequestSecondaryDocument = {};
      const visas = regulatory.visas;
      const greenCards = regulatory.greenCards;
      const destinationAddresses = regulatory.destinationAddresses;

      secondDocument.applicableCountryCode = arrivalCountry;

      if (visas.length === 1) {
        secondDocument.visa = this.createTravelerVisaRequest(visas[0]);
      }
      secondDocument.visaOperation = 'request';

      if (arrivalCountry === 'US') {
        secondDocument.visaOperation =
          immigrationParts.secondDocumentType !== ImmigrationSecondDocumentType.GREEN_CARD &&
          immigrationParts.secondDocumentType !== ImmigrationSecondDocumentType.NOTHING
            ? 'request'
            : 'cancel';
        if (greenCards.length === 1) {
          secondDocument.greenCard = this.createTravelerGreenCardRequest(greenCards[0]);
        }
        secondDocument.greenCardOperation =
          immigrationParts.secondDocumentType !== ImmigrationSecondDocumentType.VISA &&
          immigrationParts.secondDocumentType !== ImmigrationSecondDocumentType.NOTHING
            ? 'request'
            : 'cancel';

        if (destinationAddresses.length === 1) {
          secondDocument.address = this.createTravelerDestinationAddressRequest(destinationAddresses[0]);
        }

        secondDocument.addressOperation =
          !Boolean(destinationAddresses?.[0]?.isTransit) &&
          isStringEmpty(destinationAddresses?.[0]?.cityName) &&
          isStringEmpty(destinationAddresses?.[0]?.lines) &&
          isStringEmpty(destinationAddresses?.[0]?.zipCode) &&
          isStringEmpty(destinationAddresses?.[0]?.stateCode)
            ? 'cancel'
            : 'request';
      }

      if (!isEmptyObject(secondDocument)) {
        result.push(secondDocument);
      }
    });

    return result;
  }

  /**
   * 旅行者ビザの詳細のリクエストオブジェクトを作成します。
   * @param visa ビザの詳細。
   * @returns リクエストオブジェクト。
   */
  createTravelerVisaRequest(visa: RegulatoryDetailsVisa) {
    return {
      number: visa.number,
      expiryDate: visa.isNoExpiration
        ? DEFAULT_IMMIGRATION_EXPIRY_DATE
        : visa.expiryDate !== null
        ? this._datePipe.transform(visa.expiryDate, 'yyyy-MM-dd') ?? ''
        : '',
      isNoExpiration: visa.isNoExpiration,
    };
  }

  /**
   * 旅行者グリーン カードの詳細のリクエスト オブジェクトを作成します。
   * @param greenCard グリーンカードの詳細。
   * @returns リクエストオブジェクト。
   */
  createTravelerGreenCardRequest(greenCard: RegulatoryDetailsGreenCard) {
    return {
      number: greenCard.number,
      expiryDate: greenCard.isNoExpiration
        ? DEFAULT_IMMIGRATION_EXPIRY_DATE
        : greenCard.expiryDate
        ? this._datePipe.transform(greenCard.expiryDate, 'yyyy-MM-dd') ?? ''
        : '',
      isNoExpiration: greenCard.isNoExpiration,
    };
  }

  /**
   * 旅行者の目的地住所の詳細のリクエスト オブジェクトを作成します。
   * @param address 宛先住所の詳細。
   * @returns リクエストオブジェクト。
   */
  createTravelerDestinationAddressRequest(address: RegulatoryDetailsDestinationAddress) {
    return {
      stayOrTransit: address.isTransit ? ImmigrationStayType.TRANSIT : ImmigrationStayType.STAY_IN_US,
      line: address.lines,
      cityName: address.cityName,
      stateCode: address.stateCode,
      zipCode: address.zipCode,
    };
  }

  /**
   * マイレージプログラム情報更新のためのAPIリクエストパラメータ作成処理
   * @param passengers
   * @returns 搭乗者情報更新APIのリクエストパラメータ マイレージプログラム情報
   */
  getFrequentFlyerCardRequestParam(
    passengers: Array<PassengerInformationGroup>
  ): UpdateTravelersRequestFrequentFlyerCards {
    let frequentFlyerCards: UpdateTravelersRequestFrequentFlyerCards = {};

    passengers.forEach((passenger) => {
      frequentFlyerCards[passenger.data.travelerId] = {
        fqtv: {
          companyCode: passenger.data?.listOfMileageProgramInfo?.[0]?.mileageProgramName ?? '',
          cardNumber: passenger.data?.listOfMileageProgramInfo?.[0]?.mileageProgramNumber ?? '',
        },
      };
    });

    return frequentFlyerCards;
  }

  /**
   * 搭乗者情報系サポート情報更新のためのAPIリクエストパラメータ作成処理
   * @param passengers
   * @returns 搭乗者情報更新APIのリクエストパラメータ 搭乗者情報系サポート情報
   */
  getPassengerSupportRequestParam(
    passengers: Array<PassengerInformationGroup>
  ): UpdateTravelersRequestPassengerSupports {
    let passengerSupport: UpdateTravelersRequestPassengerSupports = {};

    passengers.forEach((passenger) => {
      passengerSupport[passenger.data.travelerId] = {
        ...(passenger.data.support.walk && {
          walking: {
            degreeSpecialServiceRequest: {
              code: passenger.data.support.walkLevel,
            },
            wheelChair: {
              isNeeded: passenger.data.support.wheelchairData?.[0]?.isNeededWheelchair ?? false,
              isFoldable: passenger.data.support.wheelchairData?.[0]?.isWheelchairFoldable ?? false,
              type: stringEmptyUndefined(passenger.data.support.wheelchairData?.[0]?.wheelchairType),
              batteryType: this._s04CommonSvc.createBatteryRequest(
                passenger.data.support.wheelchairData?.[0]?.wheelchairBatteryType ?? ''
              ),
              depth: numberZeroUndefined(Number(passenger.data.support.wheelchairData?.[0]?.wheelchairDepth ?? 0)),
              width: numberZeroUndefined(Number(passenger.data.support.wheelchairData?.[0]?.wheelchairWidth ?? 0)),
              height: numberZeroUndefined(Number(passenger.data.support.wheelchairData?.[0]?.wheelchairHeight ?? 0)),
              weight: numberZeroUndefined(Number(passenger.data.support.wheelchairData?.[0]?.wheelchairWeight ?? 0)),
            },
          },
        }),
        walkingOperation: passenger.data.support.walk ? 'request' : 'cancel',
        ...(passenger.data.support.isPregnant && {
          pregnant: {
            doctorName: passenger.data.support?.pregnantDoctorName,
            doctorPhone: {
              countryPhoneExtension: passenger.data.support?.pregnantDoctorCountryPhoneExtension,
              number: passenger.data.support?.pregnantDoctorPhoneNumber,
            },
            expectedDateOfBirth:
              passenger.data.support?.pregnantDueDate && !isNaN(passenger.data.support.pregnantDueDate.getTime())
                ? this._datePipe.transform(passenger.data.support.pregnantDueDate, 'yyyy-MM-dd') ?? ''
                : '',
            condition: passenger.data.support?.pregnantCondition,
          },
        }),
        pregnantOperation: passenger.data.support.isPregnant ? 'request' : 'cancel',
        blind: passenger.data.support.blind,
        deaf: passenger.data.support.deaf,
      };
    });

    return passengerSupport;
  }

  /**
   * (44.8)
   * endpointId: /orders/update-travelers
   * method: post
   * @param requestParam 搭乗者情報更新APIリクエストデータ型
   * @returns @see UpdateTravelersResult
   */
  async callUpdateTravelerApi(requestParam: UpdateTravelersRequest): Promise<UpdateTravelersResult> {
    const request: ApiCommonRequest & UpdateTravelersRequest = {
      ...requestParam,
      commonIgnoreErrorFlg: true,
    };

    const apiResponse = await this.callApi(this._servicingApiService.ordersUpdateTravelersPost(request));

    // (44.9) エラーが発生した搭乗者情報更新レスポンスが通知された場合、条件に応じて処理を行う。
    if (apiResponse.error) {
      const retryableError = this.handleUpdateTravelersError();
      const code = this._common.apiError?.errors?.[0]?.code ?? '';

      // (44.9.3) APIレスポンス.errorsが“EBAZ000421”(非常口座席を選択している搭乗者が、該当のサポート情報登録不可エラー)に該当する場合、
      if (!retryableError && code === ErrorCodeConstants.ERROR_CODES.EBAZ000421) {
        // エラーメッセージID="E0478"(非常口座席を指定している場合にリクエストできないサポートである旨)を 注意喚起エリア store サービスに保存し、
        this._common.alertMessageStoreService.setAlertWarningMessage({
          apiErrorCode: code,
          errorMessageId: 'E0478',
          contentHtml: 'E0478',
          isCloseEnable: true,
        });

        return {
          isSuccess: false,
          forceProcessEnd: true,
          apiErrorCode: code,
          errorMsgId: 'E0478',
        };
      } else if (!retryableError) {
        const code = this._common.apiError?.errors?.[0]?.code ?? '';
        const error = UPDATE_TRAVELERS_API_ERROR_MAP[code];

        // 想定外のエラーの場合
        if (isEmptyObject(error)) {
          this._common.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.SYSTEM,
            apiErrorCode: code,
          });
        }
      }

      return {
        isSuccess: false,
        forceProcessEnd: false,
        apiErrorCode: retryableError?.apiErrorCode,
        errorMsgId: retryableError?.errorMsgId,
      };
    }

    return {
      isSuccess: true,
      forceProcessEnd: false,
      warnings: apiResponse?.model?.warnings,
    };
  }

  /**
   * (44.9) エラーが発生した搭乗者情報更新レスポンスが通知された場合、条件に応じて処理を行う。
   * ※当処理はstoreを介して行う。エラーが発生した搭乗者情報更新レスポンスが通知されたことを契機に処理を行うようにする。
   * @returns 再試行可能なエラー、またはエラーが再試行できない場合は未定義。
   */
  handleUpdateTravelersError(): RetryableError | undefined {
    const code = this._common.apiError?.errors?.[0]?.code ?? '';
    const error = UPDATE_TRAVELERS_API_ERROR_MAP[code];

    // 9.1. APIレスポンス.errorsが以下の表の場合、継続不可能エラータイプ＝”bussinessLogic”(ビジネスロジックエラー)、
    // エラーメッセージIDにAPIレスポンス.errorsに応じたエラーメッセージIDにて継続不可能なエラー情報を指定し、処理を終了する。
    if (!isEmptyObject(error) && error.isRetryableError === false) {
      this._common.errorsHandlerService.setNotRetryableError({
        errorType: ErrorType.BUSINESS_LOGIC,
        errorMsgId: error.errorMsgId,
        apiErrorCode: code,
        params: error.replaceParams ?? undefined,
      });

      return undefined;
    }

    // 9.2. APIレスポンス.errorsが以下の表の場合、エラーメッセージIDにAPIレスポンス.errorsに応じた。例外の場合は、この処理をスキップします。
    // メッセージID(※下表参照)を指定した継続可能なエラー情報として設定し、当画面に表示する。
    if (!isEmptyObject(error) && error.isRetryableError === true) {
      const retryableError = {
        errorMsgId: error.errorMsgId,
        apiErrorCode: code,
        params: error.replaceParams,
      };

      this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, retryableError);

      return retryableError;
    }

    // 例外の場合は、この処理をスキップします。
    return undefined;
  }

  /**
   * 代表者連絡先情報 初期値作成
   * @param pnr PNR情報
   * @returns 代表者連絡先情報作成 初期値
   */
  createRepresentativeInformationData(pnr?: GetOrderResponseData): RepresentativeInformationData {
    const representative = pnr?.contacts?.representative ?? {};

    const representativeData = this._representativeInfoService.createData(representative);

    return representativeData;
  }

  /**
   * (1.5.1.1) 表示用代表者連絡先情報を設定する。
   * @param pnr PNR情報
   * @returns 代表者連絡先情報作成設定値 初期値
   */
  createRepresentativeInformationParts(pnr?: GetOrderResponseData): RepresentativeInformationParts {
    const representative = pnr?.contacts?.representative ?? {};
    const emails = representative.emails ?? [];
    const phones = representative.phones ?? [];
    const orderType = pnr?.orderType;

    const representativeParts = this._representativeInfoService.createParts(representative);

    // PNR情報取得API応答.data.orderType.isGroupBooking=false(グループPNRではない)の場合、かつPNR情報取得API応答.data.orderType.channelType ≠ "nh3"(国内代理店作成PNRではない)の場合はtrue
    if (
      !Boolean(orderType?.isGroupBooking) &&
      orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3
    ) {
      representativeParts.isDisplay = true;
    }
    // 項目名: 代表者メールアドレス編集可否
    // 値: 代表者連絡先情報.emailsの要素数≧2の場合、false 上記以外、true
    representativeParts.isEmailAddressEditable = emails.length < 2;

    // 項目名: 代表者電話番号編集可否
    // 値: 代表者連絡先情報.phonesの要素数≧2の場合、false 上記以外、true
    representativeParts.isPhoneNumberEditable = phones.length < 2;

    // [2.11] 代表者電話番号情報
    // 以下のいずれかに該当する場合は非表示とする。
    const isHidePhone =
      // [2.11.1] PNR情報取得API応答.data.orderType.channelType=”GDS”
      orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds ||
      // [2.11.2] PNR情報取得API応答.data.orderType.channelDetail=”merchantNotAnaNdc”
      orderType?.channelDetail === GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantNotAnaNdc;

    representativeParts.isDisplayPhone = !isHidePhone;

    return representativeParts;
  }

  /**
   * アップグレード通知情報 初期値作成
   * @param pnr PNR情報
   * @returns アップグレード通知情報作成 初期値
   */
  createUpgradeNotificationEmailData(pnr?: GetOrderResponseData): UpgradeNotificationEmailData {
    const upgradeNotificationEmail = pnr?.contacts?.upgradeNotification ?? {};

    const upgradeNotifyEmailData = this._upgradeNotificationEmailService.createData(upgradeNotificationEmail);

    return upgradeNotifyEmailData;
  }

  /**
   * (1.5.1.3) 表示用アップグレード通知メール情報を設定する。
   * @param pnr PNR情報
   * @returns アップグレード通知情報作成設定値 初期値
   */
  createUpgradeNotificationEmailParts(pnr?: GetOrderResponseData): UpgradeNotificationEmailParts {
    const upgradeNotificationEmail = pnr?.contacts?.upgradeNotification ?? {};

    return this._upgradeNotificationEmailService.createParts(upgradeNotificationEmail);
  }

  /**
   * 搭乗者情報 初期値作成
   * @param pnr PNR情報
   * @returns 搭乗者情報作成 初期値
   */
  createPassengerInformationGroup(pnr?: GetOrderResponseData): Array<PassengerInformationGroup> {
    let result: Array<PassengerInformationGroup> = [];

    pnr?.travelers?.forEach((traveler, index) => {
      const dataGroup = {
        data: this._passengerInfoService.createData(traveler, pnr),
        parts: this._passengerInfoService.createParts(traveler, index, pnr),
      };

      // [9.15]  AMC会員情報更新チェックボックス  v.18.5
      dataGroup.parts.passportInformationParts.isDisplayUpdateAMCMember = Boolean(
        pnr?.travelersSummary?.loggedInAmcMemberTravelerId
      );

      result.push(dataGroup);
    });

    return result;
  }

  /**
   * 現地連絡先情報 初期値作成
   * @param pnr PNR情報
   * @returns 現地連絡先情報作成 初期値
   */
  createDuringStayInformationData(pnr?: GetOrderResponseData): DuringStayInformationData {
    const representative = pnr?.contacts?.representative ?? {};

    const duringStayData = this._duringStayInfoService.createData(representative);

    return duringStayData;
  }

  /**
   * (1.5.1.2) 表示用現地の連絡先情報を設定する。
   * @param pnr PNR情報
   * @returns 現地連絡先情報作成設定値 初期値
   */
  createDuringStayInformationParts(pnr?: GetOrderResponseData): DuringStayInformationParts {
    const representative = pnr?.contacts?.representative ?? {};
    const orderType = pnr?.orderType;

    const duringStayParts = this._duringStayInfoService.createParts(representative);

    // [1.16] 現地連絡先情報ブロック
    // ただし、以下のいずれかに該当する場合は非表示とする。
    const isDisplayDuringStayInformation = !(
      // [1.16.1] PNR情報取得API応答.data.orderType.isGroupBooking=true
      (
        orderType?.isGroupBooking === true ||
        // [1.16.2] PNR情報取得API応答.data.orderType.channelType ≠ 'nh'(直営PNR)、かつPNR情報取得API応答.data.orderType.channelDetail ≠ 'merchantAnaNdc'
        (orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Nh &&
          orderType?.channelDetail !== GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantAnaNdc) ||
        // [1.16.3] ユーザー共通情報.posCountryCode＝"JP”かつPNR情報取得API応答.data.air.tripType (旅程区分) ="domestic"
        (this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP' &&
          pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic)
      )
    );

    duringStayParts.isDisplay = isDisplayDuringStayInformation;

    return duringStayParts;
  }

  /**
   * 特定の旅客の発着通知データが変更されたかどうかを確認します。
   * @param passengerIndex 乗客のインデックス。
   * @param arrivalDepartureNoticeData 発着通知情報データです。
   * @returns 発着通知データが変更されたかどうかを示すブール値。
   */
  checkArrivalDepartureNoticeDataChanged(
    passengerIndex: number,
    arrivalDepartureNoticeData: ArrivalDepartureNoticeInformationData
  ) {
    const isArrDepDataChanged = arrivalDepartureNoticeData.emails.some((email, emailIndex) => {
      return Object.entries(email).some(([key, value]) => {
        if (
          isStringEmpty(
            this.listOfInitPassengerInfoData?.[passengerIndex]?.data?.arrivalAndDepartureNotice?.emails?.[emailIndex]?.[
              key as keyof ArrivalDepartureNoticeInformationEmail
            ]
          ) &&
          isStringEmpty(value)
        ) {
          return false;
        }
        return (
          this.listOfInitPassengerInfoData?.[passengerIndex]?.data?.arrivalAndDepartureNotice?.emails?.[emailIndex]?.[
            key as keyof ArrivalDepartureNoticeInformationEmail
          ] !== value
        );
      });
    });

    return isArrDepDataChanged;
  }

  /**
   * MyBookingStateを取得する
   * @returns MyBookingState Promise
   */
  getMyBookingPromise(): Promise<MyBookingState> {
    return firstValueFrom(this._store.select(selectMyBookingState).pipe(take(1)));
  }

  /**
   * (4) 代表者電話番号種別の値変更処理
   * @param value
   * @param passengers
   */
  changeRepresentativePhoneType(phoneType: PhoneTypeEventType, passengers: Array<PassengerInformationGroup>) {
    let isChanged = false;
    // (4.2) 画面.代表者電話番号種別=”M1”(携帯)から画面.代表者電話番号種別≠”M1”(携帯)へ変更した場合
    if (
      phoneType.oldPhoneType === GetOrderResponseDataContactsRepresentativePhonesInner.TypeEnum.M &&
      phoneType.newPhoneType !== GetOrderResponseDataContactsRepresentativePhonesInner.TypeEnum.M
    ) {
      passengers.forEach((passenger) => {
        passenger.parts.representativePhoneType = phoneType.newPhoneType;

        // かつ画面.SMS送信先種別＝”0”(代表者に送る)
        if (passenger.data.contact.passengerSMSDestination === MailDestinationType.REPRESENTATIVE) {
          // (4.2.1) 画面.SMS送信先種別に”1”(個人に送る)を設定する。
          passenger.data.contact.passengerSMSDestination = MailDestinationType.INDIVIDUAL;

          // (4.2.2) 搭乗者情報ブロックの編集中状況にtrue(編集中)を設定する。
          passenger.parts.registrationLabel = RegistrationLabelType.EDITING;

          isChanged = true;
        }
      });

      if (isChanged) {
        this._passengerInfoService.passengerSMSDestinationChanged$.next(true);

        // (4.3) MSG1047(SMS送信先種別が変更された)を意喚起エリア(インフォメーション)に表示する。
        this._common.alertMessageStoreService.setAlertSubInfomationMessage(SmsChangeWarningMessage);
      }

      return;
    }

    passengers.forEach((passenger) => {
      passenger.parts.representativePhoneType = phoneType.newPhoneType;
    });
  }

  /**
   * 代表者電話番号の値変更処理
   * @param phoneNumber 代表者電話番号の変更情報
   * @param passengers 各搭乗者情報ブロックのが情報含まれるリスト
   */
  changeRepresentativePhoneNumber(phoneNumber: PhoneNumberEventType, passengers: Array<PassengerInformationGroup>) {
    let isChanged = false;

    // 代表者電話番号国が日本の場合、かつ画面.代表者電話番号が070/080/090/70/80/90以外に変更された場合
    if (phoneNumber.phoneCountry === 'JP' && !this._s04CommonSvc.isSmsSendPattern(phoneNumber.newPhoneNumber)) {
      passengers.forEach((passenger) => {
        passenger.parts.representativePhoneNumber = phoneNumber.newPhoneNumber;

        // かつ画面.SMS送信先種別＝”0”(代表者に送る)
        if (passenger.data.contact.passengerSMSDestination === MailDestinationType.REPRESENTATIVE) {
          // 画面.SMS送信先種別に”1”(個人に送る)を設定する。
          passenger.data.contact.passengerSMSDestination = MailDestinationType.INDIVIDUAL;

          // 搭乗者情報ブロックの編集中状況にtrue(編集中)を設定する。
          passenger.parts.registrationLabel = RegistrationLabelType.EDITING;

          isChanged = true;
        }
      });

      if (isChanged) {
        this._passengerInfoService.passengerSMSDestinationChanged$.next(true);

        // MSG1659(SMS送信先種別が変更された)を注意喚起エリア(インフォメーション)に表示する。
        this._common.alertMessageStoreService.setAlertSubInfomationMessage(SmsChangeWarningMessageOnPhoneNumberChange);
      }

      return;
    }

    passengers.forEach((passenger) => {
      passenger.parts.representativePhoneNumber = phoneNumber.newPhoneNumber;
    });
  }

  /**
   * ダイアログを開く
   * @param messageId ダイアログのメッセージID
   * @returns @see DialogClickType
   */
  openDialogPromise(messageId: string): Promise<DialogClickType> {
    return new Promise((resolve) => {
      this._dialogService
        .openDialog({ message: MasterJsonKeyPrefix.DYNAMIC + messageId })
        .buttonClick$.subscribe((result) => resolve(result.clickType));
    });
  }

  /** @inheritdoc */
  override destroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
